import {useTheme} from "@emotion/react";
import {Slide, Slider} from "@/components/Slider";
import {Text} from "@/elements/text";
import WebpPicture from "@/elements/WebpPicture";
import type {SectionSliderImages} from "@/generated/api";
import {getSliderImage} from "@/utils/cardImage";
import {imageOf} from "@/utils/index";
import {ImgWrapper, SlideContentWrapper, SliderWrapper} from "./styles";

// Constantes pour les hauteurs d'image (synchronisées avec ImgWrapper)
const IMAGE_HEIGHT_MOBILE = 200;
const IMAGE_HEIGHT_DESKTOP = 457;
const DEFAULT_ASPECT_RATIO = 16 / 9;

interface B5SliderImagesProps {
  // The slider only reads the images and the background (B4Images hands it its own images when there are several)
  sectionData: Pick<SectionSliderImages, "images" | "bg_color">;
}

const B5SliderImages = ({sectionData}: B5SliderImagesProps) => {
  const {breakpoints} = useTheme();
  const {isMobile} = breakpoints;
  const backgroundColor = sectionData?.bg_color;

  return (
    <SliderWrapper backgroundColor={backgroundColor}>
      <Slider
        key={isMobile ? 0 : 1}
        freeMode={!isMobile}
        spaceBetween={20}
        scrollbar={{draggable: !isMobile, dragSize: isMobile ? 144 : 474}}
        style={{paddingLeft: isMobile ? "10px" : "12.5%"}}
      >
        {sectionData?.images?.map((item, i) => {
          const image = imageOf(item.image);
          const responsive = getSliderImage({image});
          // Calculer la largeur de l'image basée sur son aspect ratio et sa hauteur
          const imageHeight = isMobile ? IMAGE_HEIGHT_MOBILE : IMAGE_HEIGHT_DESKTOP;
          const imageWidth = image?.width || 0;
          const imageHeightData = image?.height || 0;

          // Calculer l'aspect ratio avec validation
          let aspectRatio = DEFAULT_ASPECT_RATIO;
          if (imageWidth > 0 && imageHeightData > 0) {
            aspectRatio = imageWidth / imageHeightData;
          }

          const calculatedWidth = imageHeight * aspectRatio;

          return (
            <Slide key={i}>
              <SlideContentWrapper style={{maxWidth: `${calculatedWidth}px`}}>
                <WebpPicture webp={image?.webp} sizes={responsive.sizes}>
                  <ImgWrapper
                    key={image?.ID ?? image?.id}
                    src={image?.url}
                    draggable="false"
                    alt={image?.alt || "slider images"}
                    title={image?.title || "slider images"}
                    {...responsive}
                    loading="lazy"
                    aspectRatioHeight={image?.height}
                    aspectRatioWidth={image?.width}
                  />
                </WebpPicture>
                <Text
                  type={"textSmallRegular"}
                  styles={{
                    marginTop: 8,
                    color: "#C0C0C0",
                    marginBottom: isMobile ? 26 : 34,
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    wordBreak: "break-word",
                    maxWidth: "100%",
                    width: "100%",
                  }}
                >
                  {image?.caption || ""}
                </Text>
              </SlideContentWrapper>
            </Slide>
          );
        })}
      </Slider>
    </SliderWrapper>
  );
};

export default B5SliderImages;
